import React, {Fragment, useMemo} from 'react'
import {
    Box,
    Text,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Divider,
    Stack,
    Container,
    Flex
} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {getContinents, getSites} from '../../../app/utils/site-utils'
import ContinentColumn from './partials/ContinentColumn'
import {ChevronDownIcon, ChevronUpIcon} from '../../../app/components/icons/index'
import {CONTINENT_MESSAGES} from '../../../app/constants'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useMultiSite from '../../hooks/use-multi-site'
import LoadingSpinner from '../../components/loading-spinner'
import HeaderContent from '../../components/header-content'
import {useCurrentCustomer} from '../../hooks/use-current-customer'

const CONTINENTS_IN_GROUP = 3

const getMappedContinents = (isProductionTestingTeamCustomer) => {
    const continents = getContinents()
    return continents
        .map((continent) =>
            groupCountryCodesDistinctByContinent(continent, isProductionTestingTeamCustomer)
        )
        .filter((continent) => continent?.continentName && continent?.countries)
}

const CountrySelector = () => {
    const {isProductionTestingTeamCustomer} = useCurrentCustomer().data
    const mappedContinents = useMemo(
        () => getMappedContinents(isProductionTestingTeamCustomer),
        [isProductionTestingTeamCustomer]
    )

    const continents = getContinents()
    const {formatMessage} = useIntl()

    const site = useMultiSite()
    const currentContinent = continents.find((continent) =>
        continent.locales.includes(site.locale.id)
    )
    const currentLocation = {
        continent: currentContinent.continentName,
        country: site.locale.id.slice(-2)
    }

    useInitialDataLayer({pageType: 'Country Selector'})

    return !mappedContinents ? (
        <LoadingSpinner />
    ) : (
        <>
            <HeaderContent />
            <Box background="background.section.subtle">
                <Container variant="no-padding">
                    <Stack
                        spacing={10}
                        px={{base: 4, md: 6, lg: 8}}
                        paddingTop={10}
                        paddingBottom={12}
                    >
                        <Text as="h1" width="100%" textStyle="Regular 45XLarge">
                            {formatMessage({
                                defaultMessage: 'Hei, where can we make your life better?',
                                id: 'country_selector_title.title.country_selector_title'
                            })}
                        </Text>
                        <Flex flexDirection="column" gap={12} display={{base: 'none', md: 'flex'}}>
                            {/* create an array for each group continents */}
                            {Array.from({
                                // round up in case last group is not full
                                length: Math.ceil(mappedContinents.length / CONTINENTS_IN_GROUP)
                            }).map((_, groupIdx, groupList) => (
                                <Fragment key={groupIdx}>
                                    <Flex key={`col-3-${groupIdx}`} gap={6} align="flex-start">
                                        {mappedContinents
                                            // get the current group of continents
                                            .slice(
                                                groupIdx * CONTINENTS_IN_GROUP,
                                                (groupIdx + 1) * CONTINENTS_IN_GROUP
                                            )
                                            .map((continent) => (
                                                <ContinentColumn
                                                    key={continent.continentName}
                                                    flex="1 1 0"
                                                    continent={continent}
                                                    currentLocation={currentLocation}
                                                />
                                            ))}
                                    </Flex>
                                    {groupIdx < groupList.length - 1 && (
                                        <Divider
                                            key={`divider-${groupIdx}`}
                                            colorScheme="stokkeBeige"
                                        />
                                    )}
                                </Fragment>
                            ))}
                        </Flex>
                        <Accordion
                            display={{base: 'flex', md: 'none'}}
                            variant="countrySelector"
                            allowMultiple
                        >
                            {mappedContinents?.map((continent, idx) => (
                                <Fragment key={idx}>
                                    {idx > 0 && (
                                        <Divider key={`divider-${idx}`} colorScheme="stokkeBeige" />
                                    )}
                                    <AccordionItem key={`item-${idx}`} border="none">
                                        {({isExpanded}) => (
                                            <>
                                                <AccordionButton key="heading">
                                                    <Text
                                                        as="h2"
                                                        textStyle="Regular 2XLarge"
                                                        alignSelf={isExpanded ? 'top' : 'center'}
                                                    >
                                                        {formatMessage(
                                                            CONTINENT_MESSAGES[
                                                                continent.continentName
                                                            ]
                                                        )}
                                                    </Text>
                                                    {isExpanded ? (
                                                        <ChevronUpIcon boxSize={4} />
                                                    ) : (
                                                        <ChevronDownIcon boxSize={4} />
                                                    )}
                                                </AccordionButton>
                                                <AccordionPanel key="content">
                                                    <ContinentColumn
                                                        key={continent.continentName}
                                                        continent={{
                                                            continentName: null,
                                                            countries: continent.countries
                                                        }}
                                                        currentLocation={currentLocation}
                                                    ></ContinentColumn>
                                                </AccordionPanel>
                                            </>
                                        )}
                                    </AccordionItem>
                                </Fragment>
                            ))}
                        </Accordion>
                    </Stack>
                </Container>
            </Box>
        </>
    )
}

const groupCountryCodesDistinctByContinent = (continent, isProductionTestingTeamCustomer) => {
    const sites = getSites()
    const countryIds = continent.locales.map((l) => l.slice(-2))
    const countries = [...new Set(countryIds)]

    const englishRegionNames = new Intl.DisplayNames(['en'], {
        type: 'region'
    })

    return {
        continentName: continent.continentName,
        countries: countries
            .map((c) => {
                const supportingSite = sites?.find(
                    (site) =>
                        site.l10n?.defaultLocale.slice(-2) === c ||
                        site.l10n?.supportedLocales.find((locale) => locale.id.slice(-2) === c)
                )
                const country = {
                    countryCode: c,
                    languageCodes: supportingSite?.l10n?.supportedLocales
                        .filter((locale) => locale.id.slice(-2) === c)
                        .map((locale) => locale.id.slice(0, 2)),
                    showBasketIcon:
                        supportingSite &&
                        (!supportingSite?.isContentOnlySite || isProductionTestingTeamCustomer),
                    siteId: supportingSite?.id,
                    englishCountryName: englishRegionNames.of(c)
                }

                country.defaultLocale =
                    supportingSite?.id === 'stokke' || supportingSite?.id === 'EUR'
                        ? country.languageCodes
                            ? `${country.languageCodes[0]}-${c}`
                            : `en-${c}`
                        : supportingSite?.l10n?.defaultLocale

                country.isLink =
                    supportingSite?.l10n?.supportedLocales.filter(
                        (locale) => locale.id.slice(-2) === c
                    ).length === 1
                return country
            })
            .sort((a, b) => a.englishCountryName.localeCompare(b.englishCountryName))
    }
}

CountrySelector.getTemplateName = () => 'country-selector'

export default CountrySelector
